/**
 * The hard page-size contract for every exhaustive CMS list fetch.
 *
 * This repo deliberately fetches CMS lists as ONE page with a hard `limit=N`
 * instead of a pagination loop — the collections are small, the fetches run
 * inside `"use cache"` scopes with a shared retry deadline, and a loop would
 * multiply both the latency and the failure surface. The price of that idiom
 * is silent truncation: the day a collection outgrows the limit, the tail
 * stops existing everywhere that list feeds, with no error anywhere.
 *
 * That has now happened twice:
 *  - AHD-9376 — 171 FAQs crossed `limit=100` unnoticed; 41% of FAQs vanished
 *    from every per-locale search index (PR #2193).
 *  - 2026-08-14 — 511 published diseases crossed `limit=500`; 11 diseases
 *    silently absent from the what-we-detect index, category pages and the
 *    related-biomarker joins until the bump to 750.
 *
 * This registry is the contract that keeps the idiom safe. For each
 * collection, the value is the SMALLEST `limit=N` any exhaustive list fetch
 * in the repo uses (so live `totalDocs <= value` implies no fetch anywhere
 * truncates). Two guards hold it from both sides:
 *
 *  - `scripts/validate-cms-non-empty.ts` probes the live CMS at build time
 *    and FAILS THE BUILD when a collection's `totalDocs` exceeds its entry
 *    (and warns from 90% headroom), so growth is caught before a truncated
 *    build ships.
 *  - `__tests__/cms-query-scan.test.ts` scans the fetching modules' source
 *    and fails when a list fetch uses a limit SMALLER than the entry (or hits
 *    a collection missing from the registry), so a new fetch cannot dodge the
 *    build-time guard.
 *
 * When a collection legitimately grows past its entry: bump the fetch sites
 * AND the entry together (the scan test holds them in sync). When adding a
 * deliberate top-N fetch (a capped section, not an exhaustive list), register
 * it in the scan test's CAPPED_LISTS_BY_DESIGN instead of lowering the entry.
 *
 * Two blind spots, stated rather than hidden:
 *
 *  - DRAFTS. The build-time probe counts what Payload's default (published)
 *    read returns, while `draft=true` preview fetches see published PLUS
 *    draft versions. A collection with many never-published drafts can
 *    therefore truncate its PREVIEW list (e.g. `getPreviewFaqItems` at
 *    limit=200) before the probe warns. Editors notice a missing draft where
 *    users never would, so the published count is the guarded one — but when
 *    a preview list looks short, check draft volume before anything else.
 *
 *  - NO LIMIT AT ALL. A list query that omits `limit=` truncates at
 *    Payload's DEFAULT of 10 with no error — invisible to a registry keyed
 *    on written limits. The cms-query-scan sweep bans limit-less CMS list
 *    queries outright for exactly that reason.
 */
export const HARD_LIST_LIMITS: Record<string, number> = {
  authors: 50,
  biomarkers: 300,
  "blog-posts": 50,
  categories: 100,
  cities: 100,
  diseases: 750,
  faq: 200,
  "faq-categories": 100,
  "insurance-packages": 500,
  insurers: 100,
  "job-listings": 50,
  "landing-pages": 200,
  locations: 100,
  "pricing-variables": 500,
  // The PIM, and since AHD-12802 phase 1 the per-country service control
  // plane read by `getServicesConfig`. Unlike `services`, `products` carries
  // drafts and trashed rows, so `totalDocs` on the unfiltered headroom probe
  // counts more than the published set the site reads. 100 is the tightest of
  // the two `products` fetches (`lib/cms-products.ts`), and ~5x the catalogue.
  products: 100,
  // No module fetches `/services` directly any more (phase 1 moved the control
  // plane to `products`), so this entry buys no truncation guard. It stays for
  // the OTHER half of what the registry drives: `validate-cms-non-empty.ts`
  // iterates these keys and probes each collection at build time, and clinic
  // pages still render `locations.imagingServices[].service` /
  // `bloodServices[].service` through the locations relation. Dropping it would
  // remove the only build-time signal that the collection has gone away or
  // stopped answering, while pages still depend on it. Delete this together
  // with the clinic-page switch in phase 2. `cms-query-scan` allows it via
  // REGISTRY_COVERED_DYNAMICALLY.
  services: 200,
  "symptom-groups": 50,
  symptoms: 200,
  testimonials: 200,
}

/** Warn (without failing the build) once a collection uses this share of its
 *  registered limit — the nudge to bump the fetch sites before growth becomes
 *  an incident. */
export const LIMIT_WARN_RATIO = 0.9
